import { ref } from 'vue';

const REALTIME_OPERATION = 'realtime_enabled';
const PAUSE_DURATION_MS = (4 * 60 + 20) * 1000;       // 4 minutes 20 seconds
const PAUSE_DEADLINE_KEY = 'realtime_pause_deadline'; // clock time the countdown ends (ms)

// Seconds left, shown in the UI (0 when the countdown isn't running).
export const remainingSeconds = ref(0);

let vuexStore = null;
let countdownIntervalId = null;

const getCurrentUser = () => vuexStore?.state?.user?.cu ?? null;
const isRealtimeEnabled = () => !!getCurrentUser()?.own_allowed_operations?.includes(REALTIME_OPERATION);

const readPauseDeadline = () => Number(localStorage.getItem(PAUSE_DEADLINE_KEY)) || null;
const savePauseDeadline = (endsAtMs) => {
  if (endsAtMs) localStorage.setItem(PAUSE_DEADLINE_KEY, String(endsAtMs));
  else localStorage.removeItem(PAUSE_DEADLINE_KEY);
};

const reEnableRealtime = async () => {
  const user = getCurrentUser();
  const userId = user?.original_id || user?.id; // the employee's own record, not the shared pharmacy one
  if (!userId || !window.$crm || isRealtimeEnabled()) return;

  const allowed_operations = [...(user.own_allowed_operations || []), REALTIME_OPERATION];
  vuexStore.commit('user/cu', { ...user, own_allowed_operations: allowed_operations });
  try {
    await window.$crm.items('directus_users').updateOne(userId, { allowed_operations }, { fields: ['allowed_operations'] });
  } catch (error) {
    console.error('[realtime-timer] auto re-enable failed:', error);
  }
};

const stopCountdown = () => {
  clearInterval(countdownIntervalId);
  countdownIntervalId = null;
  savePauseDeadline(null);
  remainingSeconds.value = 0;
};

// Runs once a second while paused.
const updateCountdown = async () => {
  const secondsLeft = Math.ceil(((readPauseDeadline() || 0) - Date.now()) / 1000);
  if (secondsLeft > 0) {
    remainingSeconds.value = secondsLeft;
    return;
  }
  stopCountdown();
  await reEnableRealtime();
};

const startCountdown = () => {
  if (!readPauseDeadline()) savePauseDeadline(Date.now() + PAUSE_DURATION_MS); // fresh, or keep the saved one
  if (!countdownIntervalId) {
    updateCountdown();
    countdownIntervalId = setInterval(updateCountdown, 1000);
  }
};

// Run the countdown while paused, stop it otherwise. Called on every user change.
const syncCountdownWithRealtimeState = () => {
  if (getCurrentUser()?.id && !isRealtimeEnabled()) startCountdown();
  else stopCountdown();
};

// Refresh button: restart the countdown from a full 5 minutes.
export const resetPauseTimer = () => {
  if (getCurrentUser()?.id && !isRealtimeEnabled()) {
    savePauseDeadline(Date.now() + PAUSE_DURATION_MS);
    startCountdown();
  }
};

// Wire up once at boot.
export const registerRealtimePauseTimer = ({ store }) => {
  vuexStore = store;
  vuexStore.watch((state) => state.user.cu, syncCountdownWithRealtimeState);
};

// Format seconds as m:ss for the UI.
export const formatCountdown = (seconds) => {
  const safeSeconds = Math.max(0, Math.floor(seconds));
  return `${Math.floor(safeSeconds / 60)}:${String(safeSeconds % 60).padStart(2, '0')}`;
};
